import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import "./MicrositePreview.css";
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';

const MicrositePreview = () => {
  const { orgId, eventId } = useParams();
  const [eventData, setEventData] = useState(null);
  const [description, setDescription] = useState("");
  const [bannerUrl, setBannerUrl] = useState(null);
  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  const GOOGLE_MAPS_API_KEY = "AIzaSyCN0ylKmtKK6Vz9e90PZOOb1kNpDWdigF4";
  const FALLBACK_IMAGE = "https://via.placeholder.com/1200x400?text=Event+Coming+Soon";

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const response = await axios.get(
          `https://us-central1-integrity-resolutions-inc.cloudfunctions.net/getEvent`,
          {
            headers: {
              Authorization: `Bearer dummy-preview-token`,
              "organization-id": orgId,
            },
            params: { eventId, preview: true },
          }
        );

        const data = response.data?.eventData;
        setEventData(data);

        const config = data?.micrositeConfig || {};

        // Description
        if (data?.description) {
          setDescription(data.description);
        } else if (data?.eventName && data?.eventType) {
          try {
            const aiResp = await axios.post(
              `https://generateeventdescription-774073159618.us-central1.run.app`,
              {
                eventType: data.eventType,
                eventName: data.eventName,
                fundraisingGoal: data.fundraisingGoal,
                location: data.location,
                startDate: data.startDate,
                targetAudience: data.targetAudience,
                userDescription: "",
                userEmail: "",
                organizationId: orgId,
              }
            );
            const aiDescription = aiResp.data?.description;
            if (aiDescription) setDescription(aiDescription);
          } catch (genErr) {
            console.warn("AI description fallback failed:", genErr.message);
          }
        }

        // Background Image (from micrositeConfig)
        if (config.backgroundImageUrl) {
          setBackgroundUrl(config.backgroundImageUrl);
        }

        // Banner Image (Only if no background image)
        if (data?.eventImage?.trim()) {
          setBannerUrl(data.eventImage);
        } else if (!config.backgroundImageUrl) {
          const bannerResp = await axios.post(
            `https://generateeventbannerimage-774073159618.us-central1.run.app`,
            {
              preview: true,
              eventId: data.eventId,
              organizationId: orgId,
              eventName: data.eventName,
              eventType: data.eventType,
              startDate: data.startDate,
            }
          );

          if (bannerResp.data?.bannerUrl) {
            setBannerUrl(bannerResp.data.bannerUrl);
          } else {
            setBannerUrl(FALLBACK_IMAGE);
          }
        }
      } catch (error) {
        console.error("Error loading preview:", error);
        setEventData(null);
        setBannerUrl(FALLBACK_IMAGE);
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [orgId, eventId]);

  if (loading) return <div className="loading">Loading Preview...</div>;
  if (!eventData) return <div className="error">Event not found.</div>;

  const config = eventData.micrositeConfig || {};

  const formattedDate = eventData.startDate
    ? new Date(eventData.startDate).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })
    : null;

  const formattedTime = eventData.startTime
    ? new Date(eventData.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
    : null;

  const mapSrc =
    eventData.locationLat && eventData.locationLng
      ? `https://www.google.com/maps/embed/v1/view?key=${GOOGLE_MAPS_API_KEY}&center=${eventData.locationLat},${eventData.locationLng}&zoom=14`
      : null;

      return (
        <div
          className="microsite-container"
          style={{
            backgroundColor: config.backgroundColor || "#ffffff",
            backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            color: config.textColor || "#000000",
            transition: "all 0.3s ease",
            minHeight: "100vh",
          }}
        >
          {/* Conditionally render Banner */}
          {bannerUrl && !backgroundUrl && (
            <div className="microsite-banner-container">
              <img
                src={bannerUrl}
                alt="Event Banner"
                className="banner"
                onError={(e) => {
                  console.warn("Banner failed to load. Using fallback.");
                  e.target.src = FALLBACK_IMAGE;
                }}
              />
            </div>
          )}
      
          {/* Event Info Section */}
          <div className="microsite-content">
            <div
              style={{
                backgroundColor: config.textShadingColor
                  ? `rgba(${config.textShadingColor.r},${config.textShadingColor.g},${config.textShadingColor.b},${config.textShadingColor.a})`
                  : "transparent",
                padding: "1.5rem",
                borderRadius: "8px",
                textAlign: "center",
                marginBottom: "1rem",
                transition: "background-color 0.5s ease",
              }}
            >
              <h1 className="event-title" style={{ color: config.textColor }}>
                {eventData.eventName}
              </h1>
              <h3 className="event-type" style={{ color: config.textColor }}>
                {eventData.eventType}
              </h3>
      
              {formattedDate && (
                <div className="event-detail" style={{ marginTop: "0.5rem" }}>
                  <span className="event-icon">📅</span>
                  <span>
                    {formattedDate}
                    {formattedTime && ` at ${formattedTime}`}
                  </span>
                </div>
              )}
      
              {eventData.location && (
                <div className="event-detail">
                  <span className="event-icon">📍</span>
                  <span>{eventData.location}</span>
                </div>
              )}
            </div>
      
            {/* Google Map */}
            {mapSrc && (
              <div className="event-map">
                <iframe
                  title="Event Map"
                  width="100%"
                  height="250"
                  style={{ border: 0, marginTop: "1rem" }}
                  loading="lazy"
                  allowFullScreen
                  src={mapSrc}
                ></iframe>
              </div>
            )}
      
            <hr />
      
            {/* Welcome Message and Headline */}
            <h2 style={{ color: config.textColor }}>
              {config.welcomeMessage || "Welcome to our event!"}
            </h2>
      
            <h3 style={{ color: config.textColor }}>
              {config.donationHeadline || ""}
            </h3>
      
            {/* CTA and Donate Buttons */}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "1rem",
                marginTop: "1rem",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {config.callToAction && (
                <button className="cta-button">{config.callToAction}</button>
              )}
              {config.includeDonateButton && (
                <button className="cta-button secondary">Donate Now</button>
              )}
            </div>
      
            {/* Secondary CTA */}
            {config.secondaryCTA && (
              <p className="secondary-cta" style={{ marginTop: "1rem" }}>
                {config.secondaryCTA}
              </p>
            )}
      
            <hr />
      
            {/* Event Description */}
            {description && (
              <div className="event-description">
                <h4>About the Event</h4>
                <p>{description}</p>
              </div>
            )}
      
            {/* Contact Info */}
            {eventData.contact?.name && (
              <div className="contact-info">
                <h4>Contact Us</h4>
                <p>{eventData.contact.name}</p>
                {eventData.contact.email && <p>Email: {eventData.contact.email}</p>}
                {eventData.contact.phone && <p>Phone: {eventData.contact.phone}</p>}
              </div>
            )}
      
            {/* Registration Deadline */}
            {eventData.registrationDeadline && (
              <div className="registration-deadline">
                <h5>Register By:</h5>
                <p>{new Date(eventData.registrationDeadline).toLocaleDateString()}</p>
              </div>
            )}
      
            {/* Tags */}
            {Array.isArray(eventData.eventTags) && eventData.eventTags.length > 0 && (
              <div className="tags">
                <h5>Tags</h5>
                <ul>
                  {eventData.eventTags.map((tag, idx) => (
                    <li key={idx}>{tag}</li>
                  ))}
                </ul>
              </div>
            )}
      
            {/* Inline Share Bar */}
            <div className="share-bar-inline">
            <h4>Share This Event</h4>
            <div className="share-icons">
                <a href={`sms:?&body=Check out this event: ${window.location.href}`} target="_blank" rel="noopener noreferrer">
                <SmsIcon />
                </a>
                <a href={`mailto:?subject=Check out this event&body=${window.location.href}`} target="_blank" rel="noopener noreferrer">
                <EmailIcon />
                </a>
                <a href={`https://twitter.com/intent/tweet?text=Check out this event&url=${window.location.href}`} target="_blank" rel="noopener noreferrer">
                <TwitterIcon />
                </a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`} target="_blank" rel="noopener noreferrer">
                <FacebookIcon />
                </a>
            </div>
            </div>
          </div>
        </div>
      );
};

export default MicrositePreview;